/**
 * Scoped query invalidation for the right-rail page filters.
 *
 * BACKGROUND (founder 2026-07-06 — "the sidebar filter doesn't work, sometimes
 * takes ~10s, sometimes blanks everything out"): the rail used to call
 * `qc.invalidateQueries()` with NO argument on every toggle, which refetches
 * EVERY query in the app at once — including the setup-gate / sync / capabilities
 * queries that drive the page shell. When those flip to `isLoading`, the page
 * unmounts to a spinner for a beat (the "blank"), and waiting for the whole herd
 * to settle is the "~10 seconds". A filter toggle should only reload the current
 * page's DATA cards.
 *
 * We solve it with a DENYLIST of query-key prefixes whose result never depends on
 * the rail filters (shell / auth / sync / account-static option lists). Everything
 * NOT on the denylist is treated as filter-dependent data and refetched — so a
 * NEW data card that reads the filter keeps working without being registered here
 * (fail-open on the safe side: at worst an extra cheap refetch, never a silently
 * stale card).
 */

import type { QueryClient } from "@tanstack/react-query";
import type { Capabilities } from "./card-requirements.js";

/**
 * First segment of a react-query key (our keys are `[name, ...args]`).
 * NB: matching is EXACT on this first segment (Set membership below), never a
 * prefix/startsWith — that's deliberate. `product-skus` (the static catalog list)
 * is denylisted, but `products` / `product-summary` / `product-movers` (real,
 * filter-dependent Products-page data) are NOT, and must keep refetching. A
 * startsWith("product") would wrongly freeze all of them. Keep this exact.
 */
function firstSeg(key: unknown): string {
  return Array.isArray(key) && typeof key[0] === "string" ? key[0] : "";
}

/**
 * App-shell / gate / sync queries. Refetching ANY of these on a filter toggle is
 * what blanks the page (the gate flips to loading and remounts the shell), so
 * they're excluded from every filter/catalog invalidation below.
 */
const SHELL_PREFIXES: readonly string[] = [
  "dashboard", // setup-gate status ["dashboard","status"]
  "sync-jobs",
  "sync-pill",
  "capabilities",
  "onboarding",
  "account",
  "agency-status",
  "admin",
  "integrations",
  "integration-catalog",
  "meta-oauth-ad-accounts",
  "ui-prefs",
  "invite",
  "version",
  "version-watch",
];

/**
 * Account-static option / catalog lists the rail itself reads (the acq-options
 * dropdown values, the product hierarchy, drill option lists). These don't change
 * when you pick a filter — only when the catalog itself changes — so a plain
 * filter toggle skips them too.
 */
const STATIC_LIST_PREFIXES: readonly string[] = [
  "acq-options",
  "product-skus",
  "catalog-settings",
  "cost-readiness",
  "cm-wizard",
  "dedup",
  "drill-options",
  "drill-sources",
  "drill-products",
  "drill-discounts",
  "drill-affiliates",
];

const FILTER_INDEPENDENT = new Set<string>([...SHELL_PREFIXES, ...STATIC_LIST_PREFIXES]);
const SHELL_ONLY = new Set<string>(SHELL_PREFIXES);

/**
 * Cached payloads that can contain expense-derived numbers. A cost, catalog,
 * account-math, or connection change invalidates the reviewed financial model,
 * so none of these payloads may survive while the capability check catches up.
 *
 * Some endpoints return revenue and profit in one response. Removing the whole
 * payload is deliberate: the revenue view can refetch safely, while retaining a
 * mixed payload risks a stale CM value flashing from an already-open drill.
 */
const FINANCIAL_DERIVED_PREFIXES = new Set<string>([
  "attribution-variance",
  "breakdown",
  "card-trend",
  "customer-value",
  "dash-cac",
  "dash-ltv-alltime",
  "dash-movers",
  "dash-overview",
  "dash-overview-co",
  "dash-products",
  "dash-retention",
  "daypart",
  "exec-revenue-cm-trend",
  "fin-payback",
  "geography-cm",
  "ltv-by-affiliate",
  "ltv-by-discount",
  "ltv-by-source",
  "ltv-drill",
  "mk-cac",
  "mk-overview",
  "mk-overview-co",
  "mk-rev-channel",
  "mk-top-products",
  "ops-profit-model",
  "ops-retention",
  "product-ltv-lift",
  "product-movers",
  "products",
  "products-trend",
  "products-spark",
  "profit-model",
  "ratio-trend",
  "ratio-trends",
  "retention-overview",
  "sub-retention",
  "subscriptions",
]);

const REVENUE_LOCKED_CAPABILITIES: Capabilities = {
  financialBasis: "revenue",
  marketingSpend: false,
  contributionMargin: false,
  channel: false,
  email: false,
  analytics: false,
  overhead: false,
  support: false,
  fulfillment: false,
  inventory: false,
};

export function isFinancialDerivedQueryKey(key: unknown): boolean {
  return FINANCIAL_DERIVED_PREFIXES.has(firstSeg(key));
}

/**
 * Fail closed immediately after a mutation that can invalidate the reviewed
 * profit model. The cache write and removals are synchronous and intentionally
 * happen before any invalidation can start a refetch.
 */
export function relockFinancialQueries(qc: QueryClient): void {
  qc.setQueryData<Capabilities>(["capabilities"], (current) => ({
    ...(current ?? REVENUE_LOCKED_CAPABILITIES),
    financialBasis: "revenue",
    marketingSpend: false,
    contributionMargin: false,
    overhead: false,
    processingFees: false,
    customerValue: false,
    cmGap: null,
  }));
  void qc.cancelQueries({ predicate: (query) => isFinancialDerivedQueryKey(query.queryKey) });
  qc.removeQueries({ predicate: (query) => isFinancialDerivedQueryKey(query.queryKey) });

  void qc.invalidateQueries({ queryKey: ["capabilities"] });
  void qc.invalidateQueries({ queryKey: ["onboarding", "state"] });
  void qc.invalidateQueries({ queryKey: ["cost-readiness"] });
  void qc.invalidateQueries({ queryKey: ["dashboard", "status"] });
  void qc.invalidateQueries({ queryKey: ["action-items"] });
  void qc.invalidateQueries({ queryKey: ["data-trust-warnings"] });
  void qc.invalidateQueries({ queryKey: ["verify-trust"] });
}

/**
 * Refetch only the DATA queries that depend on the page-rail filters (product /
 * source / affiliate / discount). Use this everywhere a rail selection changes —
 * it reloads just the current page's cards, fast, and never blanks the shell.
 */
export function invalidateFilterQueries(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({
    predicate: (q) => !FILTER_INDEPENDENT.has(firstSeg(q.queryKey)),
  });
}

/**
 * Broader refetch for a CATALOG change (e.g. grouping SKUs into a product from the
 * LTV table): the product hierarchy + SKU lists MUST refetch too, so this only
 * spares the shell/gate/sync queries — the page can't blank mid-action, but every
 * "by product" surface still updates ("save everywhere").
 */
export function invalidateCatalogQueries(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({
    predicate: (q) => !SHELL_ONLY.has(firstSeg(q.queryKey)),
  });
}
